import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import {
  getAuthMode,
  isBrowserAuthenticated,
  getBrowserAuthUser,
  mockTelegramEnvForBrowserUser,
  AuthMode,
} from '@/utils/telegram-environment';
import { BrowserLoginPage } from '@/pages';
import { AuthProvider } from '@/providers';
import App from '../App';
import { Toaster } from 'sonner';

/**
 * AppBootstrap Component
 * 
 * Handles the initial app bootstrapping process:
 * 1. Detects if running in Telegram or browser
 * 2. If browser mode and not authenticated, shows login page
 * 3. If browser mode and authenticated, mocks Telegram SDK and loads app
 * 4. If telegram mode, loads app normally
 */
const AppBootstrap: React.FC = () => {
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isBrowserLoggedIn, setIsBrowserLoggedIn] = useState(false);

  const initialize = useCallback(() => {
    const mode = getAuthMode();
    setAuthMode(mode);

    console.log('[AppBootstrap] Detected auth mode:', mode);

    if (mode === 'browser') {
      // Check if already authenticated in browser mode
      const isAuthenticated = isBrowserAuthenticated();
      
      if (isAuthenticated) {
        const user = getBrowserAuthUser();
        if (user) {
          console.log('[AppBootstrap] Browser user already authenticated, mocking SDK');
          mockTelegramEnvForBrowserUser(user);
          setIsBrowserLoggedIn(true);
        }
      }
    }

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleBrowserLoginSuccess = useCallback(() => {
    console.log('[AppBootstrap] Browser login successful, reloading app');
    // Re-initialize to pick up the new authentication state
    setIsBrowserLoggedIn(true);
  }, []);

  const handleBrowserAuthRequired = useCallback(() => {
    console.log('[AppBootstrap] Browser auth required, showing login page');
    // Clear browser logged in state to show login page
    setIsBrowserLoggedIn(false);
  }, []);

  // Show loading while detecting environment
  if (!isInitialized || authMode === null) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
          <p className="text-text-primary">Initializing...</p>
        </div>
      </div>
    );
  }

  // Browser mode: Show login page if not authenticated
  if (authMode === 'browser' && !isBrowserLoggedIn) {
    return (
      <>
        <BrowserLoginPage onLoginSuccess={handleBrowserLoginSuccess} />
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: 'var(--tg-theme-secondary-bg-color, #232e3c)',
              color: 'var(--tg-theme-text-color, #f5f5f5)',
              border: '1px solid var(--tg-theme-hint-color, #708499)',
            },
          }}
        />
      </>
    );
  }

  // Telegram mode OR Browser mode with authentication: Load the main app
  return (
    <AuthProvider onBrowserAuthRequired={handleBrowserAuthRequired}>
      <App />
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--tg-theme-secondary-bg-color, #232e3c)',
            color: 'var(--tg-theme-text-color, #f5f5f5)',
            border: '1px solid var(--tg-theme-hint-color, #708499)',
          },
        }}
      />
    </AuthProvider>
  );
};

export default AppBootstrap;

